/**
 * Grand Quack Auto — tunable constants.
 *
 * Per research.md §5: "your config.js file is secretly the most important file
 * in the project — every speed, size, color, and threshold lives there, letting
 * you balance the entire game by tweaking one file."
 *
 * ALL magic numbers go here. Do not hardcode values in systems/entities — import
 * from CONFIG instead. This is the load-bearing convention of the whole codebase.
 *
 * Section references below map to research.md sections.
 */

// -----------------------------------------------------------------------------
// § Game loop (research §5 .cursorrules)
// -----------------------------------------------------------------------------
export const FIXED_DT = 1 / 60;
/** Spiral-of-death cap: clamp per-frame delta to prevent tab-resume blowups. */
export const MAX_FRAME_DT = 0.25;

// -----------------------------------------------------------------------------
// § World (research §3)
// -----------------------------------------------------------------------------
export const GROUND_SIZE = 100;
export const CITY_BLOCK_GRID = 6; // 6×6 city block grid — research §3
export const CITY_BLOCK_SIZE = 12;
export const ROAD_WIDTH = 4;
export const BUILDING_HEIGHT = { min: 6, max: 15 } as const;

// -----------------------------------------------------------------------------
// § Palette (research §2 — pastel toon city)
// -----------------------------------------------------------------------------
export const PALETTE = {
  duck: 0xffd700,
  duckBeak: 0xff9933,
  duckEye: 0xffffff,
  ground: 0x4a7c4e,
  buildingA: 0x88aacc,
  buildingB: 0xcc8888,
  buildingC: 0xcccc88,
  buildingD: 0x88cc88,
  buildingE: 0xccaa88,
  road: 0x555555,
  roadLine: 0xffffff,
  skyFog: 0xddeeff,
  policeRed: 0xff0000,
  policeBlue: 0x0044ff,
  explosion: 0xff6600,
  shadow: 0x443355,
  ambient: 0x8899bb,
  duckPupil: 0x111111,
} as const;

// -----------------------------------------------------------------------------
// § Lighting (research §2)
// -----------------------------------------------------------------------------
export const LIGHTING = {
  sunPosition: [50, 80, 30] as const,
  sunIntensity: 2.0,
  shadowMapSize: 2048,
  shadowCameraRadius: 60,
  ambientIntensity: 0.4,
  hemisphereSky: 0x87ceeb,
  hemisphereGround: 0x556633,
  hemisphereIntensity: 0.3,
} as const;

// § Fog (research §2)
export const FOG_DENSITY = 0.012;

// § Post-processing (research §2 — bloom)
export const POST_PROCESSING = {
  bloomStrength: 0.3,
  bloomThreshold: 0.85,
  bloomRadius: 0.4,
} as const;

// § Neon night system — Blade Runner 2049 vibes
export const NEON = {
  /** Road edge glow strip dimensions. */
  stripWidth: 0.15,
  stripHeight: 0.06,
  stripColors: [0x00ffff, 0xff00ff, 0x00ff88] as readonly number[],
  /** Duck emits golden glow at night. */
  duckGlowColor: 0xffd700,
  duckGlowIntensity: 3,
  duckGlowDistance: 15,
  /** Street lights at intersections. */
  streetColors: [0x00ffff, 0xff3ea8, 0x88ff00, 0xff6600] as readonly number[],
  streetIntensity: 4,
  streetDistance: 25,
  streetHeight: 6,
  maxStreetLights: 8,
  /** Night bloom overrides (lerped to during dusk). */
  nightBloomStrength: 0.7,
  nightBloomThreshold: 0.45,
  nightBloomRadius: 0.6,
  /** Cycle-time thresholds for neon fade in/out. */
  duskStart: 0.60,
  duskEnd: 0.72,
  dawnStart: 0.95,
  dawnEnd: 1.0,
  /** Building window glow. */
  windowSize: 0.5,
  windowsPerFace: 3,
  windowColors: [0xffd700, 0x00ffff, 0xff3ea8, 0xffffff, 0x88ff00] as readonly number[],
} as const;

// -----------------------------------------------------------------------------
// § Camera (research §3 — third-person chase)
// -----------------------------------------------------------------------------
export const CAMERA = {
  fovMin: 60,
  fovMax: 75,
  near: 0.1,
  far: 500,
  offset: { x: 0, y: 5, z: -8 } as const,
  /** Exponent for position smoothing — smaller = snappier. */
  followLerp: 0.001,
  /** Exponent for camera yaw orbit — larger = lazier (GTA/Goat Sim feel). */
  yawSmoothing: 0.1,
  lookHeadOffset: 0.6,
} as const;

// -----------------------------------------------------------------------------
// § Duck movement (research §5 session prompts)
// -----------------------------------------------------------------------------
export const DUCK = {
  walkSpeed: 4.0,
  runSpeed: 7.0,
  /** Lerp factor for rotation toward heading — higher = snappier. */
  turnLerp: 0.35,
  height: 0.8,
  bodyRadius: 0.5,
  beakLength: 0.2,
  beakWidth: 0.22,
  headRadius: 0.22,
  wingSize: 0.12,
  tailHeight: 0.15,
  footRadius: 0.12,
  /** Physics body mass — light so bumps by cars don't snag the player. */
  bodyMass: 1.0,
  /** Linear damping — high so the duck stops fast when input is released. */
  linearDamping: 0.85,
} as const;

// -----------------------------------------------------------------------------
// § Quack ability (research §5 session 4)
// -----------------------------------------------------------------------------
export const QUACK = {
  cooldownSeconds: 2.0,
  /** Ring VFX expands 0 → this radius over ringDurationSeconds. */
  radius: 5.0,
  ringDurationSeconds: 0.5,
  /** NPCs within this distance flee when quacked. */
  scareRadius: 6.0,
} as const;

// -----------------------------------------------------------------------------
// § Tutorial / onboarding (first-time move cue + near-NPC quack prompt)
// -----------------------------------------------------------------------------
export const TUTORIAL = {
  /** Duck must move at least this many units before the move cue hides. */
  moveHideDistance: 3.0,
  /** Move cue auto-hides after this many seconds even if the duck hasn't moved. */
  moveHideElapsedSeconds: 10.0,
  /** Duck within this distance of any NPC flips to the quack prompt. */
  npcTriggerRadius: 4.5,
  /** Marker/prompt fade-out duration once the tutorial is complete. */
  markerFadeSec: 0.4,
  /** Base radial distance from duck center to each of the 4 cardinal chevrons. */
  arrowOrbitRadius: 1.6,
  /** Y offset above the duck where the chevrons float. */
  arrowHeight: 1.3,
  /** Pulse frequency (Hz) — how many times per second the chevrons push outward. */
  arrowPulseHz: 0.9,
  /** Additional outward distance at the peak of each pulse. */
  arrowPulseAmount: 0.7,
  /** Cone base radius — dial up for more visual prominence. */
  arrowSize: 0.55,
  /** NPC marker bob frequency (rad/sec input to sin). */
  markerBobFreq: 4.0,
  /** NPC marker yaw speed (rad/sec). */
  markerYawSpeed: 2.0,
} as const;

// -----------------------------------------------------------------------------
// § NPC system (research §3)
// -----------------------------------------------------------------------------
export const NPC = {
  maxActive: 25,
  walkSpeed: 1.5,
  fleeSpeedMultiplier: 3.0,
  fleeDurationSeconds: 2.0,
  /** Only simulate NPCs within this radius of the player. */
  simulationRadius: 100,
  /** Chain-panic: one screaming NPC triggers others within this radius. */
  panicChainRadius: 8,
  // ── Mega-quack ragdoll launch (Phase B2) ────────────────────────────────
  /** Mega-quack launch radius — NPCs inside this get ragdoll-blasted. */
  megaLaunchRadius: 12,
  /** Initial outward (radial XZ) speed in m/s. */
  ragdollLaunchSpeed: 6,
  /** Initial upward velocity in m/s. */
  ragdollLaunchUp: 8,
  /** Gravity applied during ragdoll, m/s² (negative = down). */
  ragdollGravity: -18,
  /** Velocity scaling on ground bounce (0=stop, 1=elastic). */
  ragdollBounceRestitution: 0.5,
  /** How long ragdoll lasts before resuming walking, in seconds. */
  ragdollDurationSeconds: 3,
  /** Max per-axis tumble angular velocity, rad/s. */
  ragdollMaxAngVel: Math.PI,
} as const;

// -----------------------------------------------------------------------------
// § Chaos / wanted system (research §3)
// -----------------------------------------------------------------------------
export const CHAOS_POINTS = {
  bump: 5,
  scare: 10,
  knockdown: 18,
  vehicleDestroy: 20,
  copHit: 45,
  ducklingDown: 25,
} as const;

export const WANTED_THRESHOLDS = {
  nuisance: 75,   // ★ (was 50 — give player more time to evolve)
  menace: 250,    // ★★
  chaosDuck: 500, // ★★★
} as const;

/** Chaos points decay per second when below menace with no cops nearby. */
export const CHAOS_DECAY_PER_SEC = 2;

// -----------------------------------------------------------------------------
// § Physics (research §3 perf budget)
// -----------------------------------------------------------------------------
export const PHYSICS = {
  gravity: -9.82,
  maxActiveBodies: 50,
  maxRagdolls: 4,
  /** Constraint solver iterations — higher = tighter contact resolution. */
  solverIterations: 15,
  /** Building collider size as a fraction of visual mesh. 1.0 = match visual
   *  exactly (no gap along walls). Too low (old 0.85) made cars visibly clip. */
  buildingColliderShrink: 1.0,
} as const;

// -----------------------------------------------------------------------------
// § Multiplayer (research §4 — "shared hallucination")
// -----------------------------------------------------------------------------
export const MULTIPLAYER = {
  /** Position sync rate — research §4 recommends 10 Hz. */
  syncHz: 10,
  /** Interpolation buffer (ms) for displaying other players in the past. */
  interpBufferMs: 100,
  maxClients: 20,
  /** Default server URL — override with VITE_SERVER_URL in .env. */
  defaultServerUrl: 'ws://localhost:2567',
  roomName: 'city',
} as const;

// § Vehicle (research §3 — RaycastVehicle)
export const VEHICLE = {
  chassisMass: 150,
  chassisHalfExtents: [1.2, 0.3, 1.8] as const, // wider + lower = stable
  wheelRadius: 0.4,
  wheelWidth: 0.25,
  // Wheel centers are OUTSIDE chassisHalfExtents.x (1.2) on purpose so the
  // wheels visibly poke past the chassis sides from any camera angle.
  // Tucking them inside (previous x=±1.0) made them invisible from front/back.
  wheelPositions: [
    [-1.25, -0.5, 1.3],   // front-left  (Y lowered so wheels visible below chassis)
    [1.25, -0.5, 1.3],    // front-right
    [-1.25, -0.5, -1.3],  // rear-left
    [1.25, -0.5, -1.3],   // rear-right
  ] as const,
  wheelSuspensionStiffness: 50,  // stiffer = less body roll
  wheelFrictionSlip: 5,          // per official demo (was 1.5)
  wheelRollInfluence: 0.005,     // even lower = harder to flip
  suspensionRestLength: 0.3,
  dampingCompression: 4.4,
  dampingRelaxation: 2.3,
  maxSuspensionTravel: 0.2,
  maxSuspensionForce: 100000,
  engineForce: 1000,
  maxSteer: 0.3,
  brakeForce: 100,
  enterRadius: 3.0,
  steerLerp: 0.15,
} as const;

// § Cop pursuit (research §3 — wanted system response)
export const COP = {
  footSpeed: 2.5,       // slower cops — give player time to enjoy chaos
  spawnDistance: 55,     // spawn further away
  maxCopsOnFoot: 2,     // legacy flat cap (unused — see maxCopsByWanted)
  /** Cops on foot per wanted level [0,1,2,3]. Scales pressure with chaos. */
  maxCopsByWanted: [0, 2, 4, 6] as const,
  pursuitUpdateHz: 4,
  /** Spread cops around the duck instead of stacking them on one point. */
  flankRadius: 3.0,
  /** When a duckling shoves a cop, cop freezes for this long (beat). */
  staggerSeconds: 0.4,
  /** Cop variants: beat (default) and riot (★★+, resists pushes). */
  kinds: {
    beat: {
      speed: 2.5,
      pushResist: 1,         // dies/staggers from a single push
      staggerSeconds: 0.4,
      bodyColor: 0x0044ff,
      bodyRadius: 0.22,
    },
    riot: {
      speed: 2.0,
      pushResist: 2,         // takes 2 pushes before staggering
      staggerSeconds: 1.0,
      bodyColor: 0x002288,    // darker uniform
      bodyRadius: 0.28,       // bulkier
    },
  },
  /** Probability a spawn at wanted ★★+ becomes a riot cop. */
  riotChanceAtMenace: 0.35,
  /** "Duckling swat" AOE — when 2+ ducklings swarm, cop winds up and pulses. */
  swatRadius: 1.2,
  swatWindupSeconds: 0.6,
  swatCooldownSeconds: 4.0,
  swatTriggerCount: 2,
} as const;

// -----------------------------------------------------------------------------
// § Duckling brood (mother-duck companion mechanic)
// -----------------------------------------------------------------------------
export const DUCKLING = {
  /** Hard cap on brood size — perf + visual readability. */
  maxBrood: 6,
  /** Ducklings hatched per successful quack. */
  spawnPerQuack: 1,
  /** Visual scale of duckling.glb relative to the duck. */
  scale: 0.45,
  /** Distance (world units) between consecutive ducklings in the trail line. */
  lineSpacing: 0.6,
  /** Cops within this distance from the mother trigger defense mode. */
  defendRadius: 8.0,
  /** Defending duckling orbit distance around target cop. */
  orbitRadius: 0.7,
  /** Push-back applies when duckling is closer than this to its target cop. */
  pushRadius: 0.55,
  /** Magnitude of the cop knockback per duckling push (m/s). */
  pushForce: 0.5,
  /** Cop within this distance from a duckling kills it. */
  killRadius: 0.35,
  /** How long Mother's Rage lasts after a duckling dies. */
  rageDurationSeconds: 3.0,
  /** Multipliers applied to QUACK during Mother's Rage. */
  rageScareRadiusMult: 1.5,
  rageCooldownMult: 0.5,
  /** Recall window: every quack flips defenders to "returning" for this long. */
  recallSeconds: 1.0,
  /** Defending steering speed (m/s) — slightly faster than mother to keep up. */
  defendSpeed: 4.5,
  /** Following steering smoothing — higher = snappier. */
  followLerp: 0.35,
} as const;

// -----------------------------------------------------------------------------
// § Duckling animation (waddle, bob, sway — visual only, applied in render())
// -----------------------------------------------------------------------------
export const DUCKLING_ANIM = {
  /** Vertical bounce frequency while following (Hz). */
  bobFrequency: 8.0,
  /** Vertical bounce amplitude (world units — 3 cm). */
  bobAmplitude: 0.03,
  /** Lateral sway frequency (Hz). */
  swayFrequency: 4.0,
  /** Z-axis waddle tilt (radians). */
  waddleTilt: 0.12,
  /** Yaw smoothing factor per fixedUpdate tick (lower = smoother turn). */
  rotationLerp: 0.15,
} as const;

// § Duckling ambient chirps — scales with brood size
export const DUCKLING_CHIRP = {
  /** Base interval (seconds) between chirps — decreases with brood size. */
  baseInterval: 0.8,
  /** Interval reduction per duckling (seconds). */
  intervalPerDuckling: 0.08,
  /** Minimum frequency (Hz). */
  freqMin: 800,
  /** Maximum frequency (Hz). */
  freqMax: 1200,
  /** Base gain (volume). */
  gainBase: 0.02,
  /** Extra gain per duckling. */
  gainPerDuckling: 0.01,
  /** Duration of a single chirp (seconds). */
  duration: 0.06,
} as const;

// § Game-over loss jingle (descending "wah wah wah waaah" trombone)
export const LOSS_SOUND = {
  /** Descending note frequencies (Hz). */
  notes: [350, 300, 250, 180] as readonly number[],
  /** Duration of each short note (seconds). */
  shortNoteDuration: 0.35,
  /** Duration of the final long note (seconds). */
  longNoteDuration: 0.7,
  /** Peak gain. */
  gain: 0.25,
  /** Bandpass filter center frequency for brassy timbre. */
  filterFreq: 800,
  filterQ: 3,
} as const;

// -----------------------------------------------------------------------------
// § Voice lines (C3 — synthesized duck voice bleats on quack)
// -----------------------------------------------------------------------------
export const VOICE = {
  /** Probability a normal quack also plays a voice bleat. */
  chance: 0.3,
  /** Frequency range for random duck-voice bleats (Hz). */
  freqMin: 200,
  freqMax: 600,
  /** Duration of a single voice bleat (seconds). */
  bleatDuration: 0.15,
  /** Number of rapid bleats per voice line (2–4 syllable quack-talk). */
  bleatCount: 3,
  /** Gap between bleats (seconds). */
  bleatGap: 0.08,
  /** Mega-quack sweep start/end frequencies. */
  megaSweepStart: 800,
  megaSweepEnd: 200,
  megaSweepDuration: 0.5,
} as const;

// § Duck animation (research §2 — juice + visual feedback)
export const DUCK_ANIM = {
  bobFrequency: 12.0,
  bobAmplitude: 0.04,
  waddleFrequency: 6.0,
  waddleAmplitude: 0.06,
  forwardLean: 0.12,
  leanSmoothing: 0.005,
  headBobFrequency: 18.0,
  headBobAmplitude: 0.08,
  wingFlapFrequency: 10.0,
  wingFlapAmplitude: 0.15,
  tailWagFrequency: 4.0,
  tailWagAmplitude: 0.1,
  idleBobAmplitude: 0.01,
  idleBobFrequency: 2.0,
} as const;

// -----------------------------------------------------------------------------
// § Day/night cycle (5-minute loop — dawn → noon → sunset → dusk)
// -----------------------------------------------------------------------------
export const DAY_NIGHT = {
  /** Full cycle length in seconds. */
  cycleDuration: 300,
  /** Start partway through so the game boots at "dawn" not midnight. */
  startOffset: 10,
  /** Sun orbit radius from world center. */
  sunOrbitRadius: 60,
  /** Peak sun elevation (noon). */
  sunElevation: 80,
  /** Sky color keyframes [normalizedTime, hex]. */
  skyColors: [
    [0.00, 0xffccaa],  // dawn — warm peach
    [0.15, 0xffddc0],  // late dawn
    [0.25, 0xddeeff],  // noon — bright blue (matches PALETTE.skyFog)
    [0.50, 0xddeeff],  // hold noon
    [0.62, 0xff9966],  // sunset — orange/pink
    [0.75, 0x664488],  // dusk — deep purple
    [0.90, 0x221133],  // night — very dark
    [1.00, 0xffccaa],  // wrap back to dawn
  ] as const,
  /** Sun light color keyframes. */
  sunColors: [
    [0.00, 0xffcc88],  // dawn — warm yellow
    [0.25, 0xffffff],  // noon — white
    [0.50, 0xffffff],  // hold noon
    [0.62, 0xff8844],  // sunset — orange
    [0.75, 0x4444aa],  // dusk — dim blue
    [0.90, 0x222244],  // night
    [1.00, 0xffcc88],  // wrap
  ] as const,
  /** Sun intensity keyframes (peaks at noon). */
  sunIntensity: [
    [0.00, 1.2],
    [0.25, 2.0],
    [0.50, 2.0],
    [0.62, 1.4],
    [0.75, 0.5],
    [0.90, 0.3],
    [1.00, 1.2],
  ] as const,
  /** Ambient light intensity keyframes. */
  ambientIntensity: [
    [0.00, 0.35],
    [0.25, 0.5],
    [0.50, 0.5],
    [0.62, 0.4],
    [0.75, 0.25],
    [0.90, 0.2],
    [1.00, 0.35],
  ] as const,
  /** Hemisphere light intensity keyframes. */
  hemiIntensity: [
    [0.00, 0.25],
    [0.25, 0.35],
    [0.50, 0.35],
    [0.62, 0.3],
    [0.75, 0.15],
    [0.90, 0.1],
    [1.00, 0.25],
  ] as const,
} as const;
